import { NetworkType } from "@layerswap/widget/types";
import { createPublicClient, encodeFunctionData, serializeTransaction, formatUnits, erc20Abi, parseGwei } from "viem";
import { publicActionsL2 } from 'viem/op-stack';
import resolveChain from "../evmUtils/resolveChain";
import { ErrorHandler, NetworkSettings } from "@layerswap/widget/internal";
import { resolveFallbackTransport } from "../evmUtils/resolveTransports";
export class EVMGasProvider {
    constructor() {
        this.getGas = async ({ address, network, token, recipientAddress = '0x2fc617e933a52713247ce25730f6695920b3befe' }) => {
            const chainId = Number(network?.chain_id);
            if (!network || !address || !chainId || !recipientAddress || !network.token) {
                return;
            }
            const contract_address = token.contract;
            try {
                const publicClient = createPublicClient({
                    chain: resolveChain(network),
                    transport: resolveFallbackTransport(network.nodes),
                });
                const getGas = network?.metadata?.evm_oracle_contract ? getOptimismGas : getEthereumGas;
                const gasProvider = new getGas({
                    publicClient,
                    chainId,
                    contract_address,
                    account: address,
                    from: network,
                    currency: token,
                    destination: recipientAddress,
                    nativeToken: network.token
                });
                const gas = await gasProvider.resolveGas();
                if (gas) {
                    return { gas, token: network.token };
                }
            }
            catch (e) {
                const error = e;
                ErrorHandler({
                    type: "GasProviderError",
                    message: error.message,
                    name: error.name,
                    stack: error.stack,
                    cause: error.cause
                });
            }
        };
    }
    supportsNetwork(network) {
        return network.type === NetworkType.EVM && !!network.token;
    }
}
class getEVMGas {
    constructor({ publicClient, chainId, contract_address, account, from, currency, destination, nativeToken, }) {
        this.constructSweeplessTxData = (txData = "0x") => {
            const hexed_sequence_number = (99999999999999999999999999999999999999999999999999999999999999999999999999999n).toString(16);
            const sequence_number_even = hexed_sequence_number?.length % 2 > 0 ? `0${hexed_sequence_number}` : hexed_sequence_number;
            return `${txData}${sequence_number_even}`;
        };
        this.publicClient = publicClient;
        this.chainId = chainId;
        this.contract_address = contract_address;
        this.account = account;
        this.from = from;
        this.currency = currency;
        this.destination = destination;
        this.nativeToken = nativeToken;
    }
    async resolveFeeData() {
        const [gasPrice, feesPerGas] = await Promise.all([
            this.getGasPrice(),
            this.estimateFeesPerGas()
        ]);
        let maxPriorityFeePerGas = feesPerGas?.maxPriorityFeePerGas;
        if (!maxPriorityFeePerGas)
            maxPriorityFeePerGas = await this.estimateMaxPriorityFeePerGas();
        let maxFeePerGas = feesPerGas?.maxFeePerGas;
        const minPriorityFeeGwei = NetworkSettings.KnownSettings[this.from.name]?.MinPriorityFeePerGasInGwei;
        if (minPriorityFeeGwei && maxPriorityFeePerGas !== undefined && maxFeePerGas !== undefined) {
            const minPriorityFee = parseGwei(minPriorityFeeGwei.toString());
            if (maxPriorityFeePerGas < minPriorityFee) {
                const diff = minPriorityFee - maxPriorityFeePerGas;
                maxPriorityFeePerGas = minPriorityFee;
                maxFeePerGas = maxFeePerGas + diff;
            }
        }
        return {
            gasPrice,
            maxFeePerGas,
            maxPriorityFeePerGas: maxPriorityFeePerGas
        };
    }
    async getGasPrice() {
        try {
            return await this.publicClient.getGasPrice();
        }
        catch (e) {
            const error = new Error(e);
            error.cause = e;
            ErrorHandler({
                type: 'GasPriceError',
                message: error.message,
                name: error.name,
                stack: error.stack,
                cause: error.cause
            });
        }
    }
    async estimateFeesPerGas() {
        try {
            return await this.publicClient.estimateFeesPerGas();
        }
        catch (e) {
            const error = new Error(e);
            error.cause = e;
            ErrorHandler({
                type: 'FeesPerGasError',
                message: error.message,
                name: error.name,
                stack: error.stack,
                cause: error.cause
            });
        }
    }
    async estimateMaxPriorityFeePerGas() {
        try {
            return await this.publicClient.estimateMaxPriorityFeePerGas();
        }
        catch (e) {
            const error = new Error(e);
            error.cause = e;
            ErrorHandler({
                type: 'MaxPriorityFeePerGasError',
                message: error.message,
                name: error.name,
                stack: error.stack,
                cause: error.cause
            });
        }
    }
    async estimateNativeGasLimit() {
        const to = this.destination;
        const gasEstimate = await this.publicClient.estimateGas({
            account: this.account,
            to: to,
            data: this.constructSweeplessTxData(),
        });
        return gasEstimate;
    }
    async estimateERC20GasLimit() {
        let encodedData = encodeFunctionData({
            abi: erc20Abi,
            functionName: "transfer",
            args: [this.destination, BigInt(1000)]
        });
        if (encodedData) {
            encodedData = this.constructSweeplessTxData(encodedData);
        }
        const estimatedERC20GasLimit = await this.publicClient.estimateGas({
            data: encodedData,
            to: this.contract_address,
            account: this.account
        });
        return estimatedERC20GasLimit;
    }
}
class getEthereumGas extends getEVMGas {
    constructor() {
        super(...arguments);
        this.resolveGas = async () => {
            const [feeData, estimatedGasLimit] = await Promise.all([
                this.resolveFeeData(),
                this.contract_address
                    ? this.estimateERC20GasLimit()
                    : this.estimateNativeGasLimit()
            ]);
            const multiplier = feeData.maxFeePerGas || feeData.gasPrice;
            if (!multiplier)
                return undefined;
            const totalGas = multiplier * estimatedGasLimit;
            const decimals = NetworkSettings.KnownSettings[this.from.name]?.FeeParsingDecimalPlaces || this.nativeToken?.decimals;
            const formattedGas = Number(formatUnits(BigInt(totalGas), decimals));
            return formattedGas;
        };
    }
}
export default class getOptimismGas extends getEVMGas {
    constructor() {
        super(...arguments);
        this.chain = resolveChain(this.from);
        this.client = createPublicClient({
            chain: this.chain,
            transport: resolveFallbackTransport(this.from.nodes),
        }).extend(publicActionsL2());
        this.resolveGas = async () => {
            const feeData = await this.resolveFeeData();
            const estimatedGasLimit = this.contract_address ?
                await this.estimateERC20GasLimit()
                : await this.estimateNativeGasLimit();
            const multiplier = feeData.maxFeePerGas || feeData.gasPrice;
            if (!multiplier || !feeData.gasPrice)
                return undefined;
            const l1OpFee = await this.GetOpL1Fee(feeData.gasPrice);
            let totalGas = (multiplier * estimatedGasLimit) + l1OpFee;
            const formattedGas = Number(formatUnits(BigInt(totalGas), this.nativeToken?.decimals));
            return formattedGas;
        };
        this.GetOpL1Fee = async (gasPrice) => {
            const amount = BigInt(1000000000000);
            let serializedTransaction;
            if (this.contract_address) {
                let encodedData = encodeFunctionData({
                    abi: erc20Abi,
                    functionName: "transfer",
                    args: [this.destination, amount]
                });
                if (encodedData) {
                    encodedData = this.constructSweeplessTxData(encodedData);
                }
                serializedTransaction = serializeTransaction({
                    client: this.client,
                    abi: erc20Abi,
                    functionName: "transfer",
                    chainId: this.chainId,
                    args: [this.destination, amount],
                    to: this.contract_address,
                    data: encodedData,
                    type: 'eip1559',
                });
            }
            else {
                serializedTransaction = serializeTransaction({
                    client: this.client,
                    chainId: this.chainId,
                    to: this.destination,
                    data: this.constructSweeplessTxData(),
                    type: 'eip1559',
                });
            }
            const fee = await this.client.estimateL1Fee({
                data: serializedTransaction,
                to: this.destination,
                account: this.account,
                gasPriceOracleAddress: this.from.metadata?.evm_oracle_contract,
                gasPrice: gasPrice
            });
            return fee;
        };
    }
}
